import React, { useEffect, useMemo, useState } from 'react'
import { gql } from 'graphql-request'
import { Eye, EyeOff, LogIn, RotateCcw, Save } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { authorizedGraphQLRequest } from '@/api/graphqlClient'
import { Button } from '@/components/ui/button'
import LoadingSpinner from '@/components/Common/LoadingSpinner'
import type { CardLanguage, LocalizedText } from '@/components/PlanCard'
import LocalizedInput, { ErrorFieldContext } from '@/pages/PlanCards/LocalizedInput'
import { LanguageSwitch } from '@/pages/PlanCards/TemplateEditor'
import { blankText, coverage, serverError } from '@/pages/PlanCards/editing'
import SignInPage from '@/pages/Auth/SignInPage'
import type { SignInBrand } from '@/pages/Auth/useSignInBrand'

/**
 * An organisation's sign-in page texts: the subtitle under its name, the lede of the
 * information panel and the contact address. The page is reached through the
 * organisation's join links and shows its name and logo regardless; a field left empty
 * shows Georgia's built-in text. Mirrors `PaywallContentPanel`, including the preview,
 * which is the very component the visitor sees.
 */

export interface LoginPageContent {
  version: 1
  subtitle?: LocalizedText
  lede?: LocalizedText
  contactEmail?: string
}

const GET_LOGIN_PAGE_CONTENT = gql`
  query getLoginPageContent($organisationId: Int!) {
    getLoginPageContent(organisationId: $organisationId)
  }
`

const SET_LOGIN_PAGE_CONTENT = gql`
  mutation setLoginPageContent($organisationId: Int!, $content: JSON) {
    setLoginPageContent(organisationId: $organisationId, content: $content)
  }
`

const EMPTY: LoginPageContent = { version: 1 }

/** Blank fields are dropped; nothing left means the built-in texts. */
const cleanContent = (content: LoginPageContent): LoginPageContent | null => {
  const out: LoginPageContent = { version: 1 }
  if (!blankText(content.subtitle)) out.subtitle = content.subtitle
  if (!blankText(content.lede)) out.lede = content.lede
  if (content.contactEmail?.trim()) out.contactEmail = content.contactEmail.trim()
  return out.subtitle || out.lede || out.contactEmail ? out : null
}

interface LoginPageSectionProps {
  organisationId: number
  organisationName: string
  /** The organisation's per-id logo URL, or null when it has none. */
  logoUrl: string | null
}

const LoginPageSection: React.FC<LoginPageSectionProps> = ({ organisationId, organisationName, logoUrl }) => {
  const { t, i18n } = useTranslation()
  const [saved, setSaved] = useState<LoginPageContent | null>(null)
  const [content, setContent] = useState<LoginPageContent>(EMPTY)
  const [language, setLanguage] = useState<CardLanguage>('en')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [showPreview, setShowPreview] = useState(false)
  const [error, setError] = useState<{ message: string; field: string | null } | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    setSuccess(null)
    authorizedGraphQLRequest<{ getLoginPageContent: LoginPageContent | null }>(GET_LOGIN_PAGE_CONTENT, { organisationId })
      .then((response) => {
        if (cancelled) return
        setSaved(response.getLoginPageContent)
        setContent(response.getLoginPageContent ?? EMPTY)
      })
      .catch((err) => !cancelled && setError(serverError(err, 'Failed to load the sign-in page texts')))
      .finally(() => !cancelled && setLoading(false))
    return () => {
      cancelled = true
    }
  }, [organisationId])

  const dirty = JSON.stringify(cleanContent(content)) !== JSON.stringify(saved)

  const save = async () => {
    setSaving(true)
    setError(null)
    setSuccess(null)
    try {
      const response = await authorizedGraphQLRequest<{ setLoginPageContent: LoginPageContent | null }>(SET_LOGIN_PAGE_CONTENT, { organisationId, content: cleanContent(content) })
      setSaved(response.setLoginPageContent)
      setContent(response.setLoginPageContent ?? EMPTY)
      setSuccess('Sign-in page texts saved.')
    } catch (err) {
      setError(serverError(err, 'Failed to save the sign-in page texts'))
    } finally {
      setSaving(false)
    }
  }

  const reset = () => {
    setContent(EMPTY)
    setError(null)
    setSuccess(null)
  }

  // The preview shows what a visitor arriving through one of this organisation's links
  // sees, built from the draft, with the built-in text where a field is empty.
  const previewBrand = useMemo<SignInBrand>(() => {
    const lang = (i18n.language || 'en').split('-')[0] as CardLanguage
    const pick = (text: LocalizedText | undefined, fallback: string) => text?.[lang]?.trim() || text?.en?.trim() || fallback
    return {
      name: organisationName,
      logoUrl,
      isGeorgia: false,
      subtitle: pick(content.subtitle, t('auth.page.subtitle')),
      lede: pick(content.lede, t('auth.page.lede')),
      contactEmail: content.contactEmail?.trim() || t('auth.page.contactEmail'),
    }
  }, [content, organisationName, logoUrl, i18n.language, t])

  const texts = [content.subtitle, content.lede].filter((x): x is LocalizedText => !!x)

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-6">
      <div className="flex items-center justify-between gap-4 mb-4">
        <div className="flex items-center gap-2">
          <LogIn className="h-5 w-5 text-gray-700" />
          <h4 className="font-semibold text-gray-800">Sign-in page</h4>
        </div>
        <LanguageSwitch value={language} onChange={setLanguage} coverage={coverage(texts)} />
      </div>
      <p className="text-sm text-gray-500 mb-6">
        People who open one of this organisation's join links see a sign-in page with its name and logo. Set the texts shown there; a field left empty shows Georgia's built-in text.
      </p>

      {error && <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm mb-4">{error.message}</div>}
      {success && <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-green-700 text-sm mb-4">{success}</div>}

      {loading ? (
        <div className="flex items-center justify-center py-8">
          <LoadingSpinner size="sm" />
        </div>
      ) : (
        <ErrorFieldContext.Provider value={error?.field ?? null}>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Subtitle</label>
              <LocalizedInput
                value={content.subtitle}
                onChange={(subtitle) => setContent({ ...content, subtitle })}
                language={language}
                path="subtitle"
                inherited={t('auth.page.subtitle')}
                tokens={false}
                optional
              />
              <p className="mt-1 text-xs text-gray-400">Under the organisation name. Up to 120 characters.</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Introduction</label>
              <LocalizedInput
                value={content.lede}
                onChange={(lede) => setContent({ ...content, lede })}
                language={language}
                path="lede"
                inherited={t('auth.page.lede')}
                tokens={false}
                optional
                multiline
              />
              <p className="mt-1 text-xs text-gray-400">The paragraph under "Login to … role play platform" Up to 400 characters.</p>
            </div>
            <div>
              <label htmlFor="login-page-contact" className="block text-sm font-medium text-gray-700 mb-1">
                Contact email
              </label>
              <input
                id="login-page-contact"
                type="email"
                value={content.contactEmail ?? ''}
                onChange={(e) => setContent({ ...content, contactEmail: e.target.value })}
                placeholder={t('auth.page.contactEmail')}
                className={`w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-1 ${error?.field === 'contactEmail' ? 'border-red-400 focus:border-red-400 focus:ring-red-400' : 'border-gray-300 focus:border-primary focus:ring-primary'}`}
                disabled={saving}
              />
              <p className="mt-1 text-xs text-gray-400">Shown under "Questions? Contact us" as a mail link.</p>
            </div>

            <div className="flex flex-wrap gap-3 pt-2">
              <Button onClick={save} disabled={saving || !dirty} variant="outline" className="border-gray-300">
                {saving ? <LoadingSpinner size="sm" /> : <Save className="h-4 w-4 mr-2" />}
                {saving ? 'Saving...' : 'Save'}
              </Button>
              <Button onClick={reset} disabled={saving} variant="outline" className="border-gray-300 text-gray-600">
                <RotateCcw className="h-4 w-4 mr-2" />
                Clear all
              </Button>
              <Button onClick={() => setShowPreview((v) => !v)} variant="outline" className="border-gray-300 text-gray-600 ml-auto">
                {showPreview ? <EyeOff className="h-4 w-4 mr-2" /> : <Eye className="h-4 w-4 mr-2" />}
                {showPreview ? 'Hide preview' : 'Preview'}
              </Button>
            </div>

            {showPreview && (
              <div className="mt-2 overflow-hidden rounded-xl border border-gray-200">
                {/* The real page, scaled to fit the panel. `min-h-dvh` inside is neutralised by the fixed frame. */}
                <div className="h-[520px] w-full overflow-hidden bg-white">
                  <div className="origin-top-left scale-[0.5]" style={{ width: '200%', height: '200%' }}>
                    <SignInPage brand={previewBrand} modules={[]} className="min-h-full">
                      <div className="flex flex-col gap-2.5">
                        <div className="h-12 rounded-xl border-[1.5px] border-[#E8EDE9] bg-white" />
                        <div className="h-12 rounded-xl border-[1.5px] border-[#E8EDE9] bg-white" />
                        <div className="h-12 rounded-xl border-[1.5px] border-[#E8EDE9] bg-white" />
                        <div className="h-12 rounded-xl bg-[#0F4C3A]" />
                      </div>
                    </SignInPage>
                  </div>
                </div>
              </div>
            )}
          </div>
        </ErrorFieldContext.Provider>
      )}
    </div>
  )
}

export default LoginPageSection
